/**
 * The reviewer's page — everything someone sees who has no Mauvely account.
 *
 * Light, deliberately. The design handoff is explicit that this is "learner
 * theme only — deliberately NOT the Qt frameless chrome", and the reason is a
 * product one: the author is in a tool, alongside Compose and Snap, while the
 * reviewer is a guest who has been sent a link and may never have heard of
 * Mauvely. This should read as a document, not as software.
 *
 * Three stages, matching the mockup:
 *
 *   1. the gate — one card: the password if the link has one, the
 *      confidentiality acknowledgement if the author left it on
 *   2. the course, with the same anchored rail the author has
 *   3. the identify step, asked once, the first time they try to comment
 *
 * Nothing here goes anywhere near `requireAuth`. `/r/*` renders before the auth
 * gate in App.tsx for exactly that reason.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, type ThreadsWire } from '../api';
import { ds } from '../ds';
import { Wordmark } from '../components/Wordmark';
import { mentionable, type Mentionable } from '../components/Composer';
import { CommentRail, type ComposeTarget } from '../components/rail/CommentRail';
import { useCourseBus } from '../components/rail/useCourseBus';
import { Avatar, initialsFor, type RailCaps } from '../components/rail/ThreadCard';
import { display, LAYOUT, mono, monoPlain, RADIUS } from '../theme';

interface LinkInfo {
  projectId: string;
  /** The course on the preview origin, signed for this link. */
  previewPath: string;
  title: string;
  slug: string;
  round: number;
  dueAt: number | null;
  lessonCount: number;
  confidential: boolean;
  /** This browser already approved the current round. */
  approvedRound?: boolean;
  /** Present when the door was a personal invite: pre-fills the identify step. */
  inviteEmail?: string;
  sharedBy: string;
}

/** What to do once a name is given: open the composer on a block, or nothing
 *  ('plain' — the write that prompted the dialog is retried by hand). */
type IdentifyTarget = { blockId: string; anchorLabel: string } | 'plain';

/** `7 Aug`, the mockup's date shape. */
const shortDate = (unix: number) => {
  const d = new Date(unix * 1000);
  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })}`;
};

export function Reviewer({ slug, token }: { slug: string; token: string }) {
  const { Button, Checkbox, Input } = ds();
  const [link, setLink] = useState<LinkInfo | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [acked, setAcked] = useState(false);
  const [entered, setEntered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  const open = useCallback(
    async (pw?: string) => {
      setError(null);
      try {
        const res = await api.links.resolve(token, pw);
        if ('needsPassword' in res && res.needsPassword) {
          setNeedsPassword(true);
          return;
        }
        setLink(res as unknown as LinkInfo);
        setNeedsPassword(false);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) setGone(true);
        else setError(e instanceof ApiError ? e.message : 'That link would not open.');
      }
    },
    [token]
  );

  useEffect(() => {
    void open();
  }, [open]);

  if (gone) {
    return (
      <Page>
        <Centered>
          <Wordmark />
          <h1 style={display(28)}>That link is no longer available.</h1>
          <p style={{ color: 'var(--text-body)', maxWidth: 420 }}>
            Ask whoever sent it for a new one.
          </p>
        </Centered>
      </Page>
    );
  }

  // ── the gate: one card, whatever it needs to ask ──────────────────────────
  if (!link || (link.confidential && !entered)) {
    const opening = !link && !needsPassword;
    return (
      <Page>
        <Centered>
          <div
            style={{
              width: 'min(520px, 100%)',
              background: 'var(--surface-card)',
              borderRadius: RADIUS.panel,
              padding: '36px 40px 28px',
              boxShadow: '0 1px 0 rgba(40,28,45,.04)',
              textAlign: 'left'
            }}
          >
            <Wordmark />
            <p style={{ ...mono, marginTop: 20 }}>Confidential review</p>
            <h1 style={{ ...display(34), marginTop: 8 }}>This course isn&rsquo;t public yet.</h1>

            {opening && <p style={{ ...monoPlain, marginTop: 14 }}>Opening</p>}

            {link ? (
              <>
                <p style={{ color: 'var(--text-body)', marginTop: 12, lineHeight: 1.65 }}>
                  {link.sharedBy} shared <strong>{link.title}</strong> with you for comment.
                  Please don&rsquo;t forward the link or share screenshots outside your team.
                </p>
                <p style={{ ...mono, marginTop: 12 }}>
                  {link.lessonCount} lessons · round {link.round}
                  {link.dueAt ? ` · due ${shortDate(link.dueAt)}` : ''}
                </p>
              </>
            ) : (
              !opening && (
                <p style={{ color: 'var(--text-body)', marginTop: 12, lineHeight: 1.65 }}>
                  Whoever sent you the link has the password.
                </p>
              )
            )}

            {needsPassword && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void open(password);
                }}
                style={{ marginTop: 16 }}
              >
                <Input
                  label="Password"
                  type="password"
                  placeholder="From the invite email"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </form>
            )}

            {link && link.confidential && (
              <div style={{ marginTop: 16 }}>
                {/* A speed-bump, not a control — docs/SECURITY.md says so. It
                    raises the cost of forwarding carelessly; it cannot stop
                    anyone who means to. */}
                <Checkbox
                  label="I understand this course is confidential and won't share it outside the review team."
                  checked={acked}
                  onChange={(next) => setAcked(next)}
                />
              </div>
            )}

            {error && <p style={{ color: 'var(--danger-500)', marginTop: 12 }}>{error}</p>}

            {!opening && (
              <div style={{ marginTop: 18 }}>
                <Button
                  size="lg"
                  fullWidth
                  disabled={link ? link.confidential && !acked : !password.trim()}
                  onClick={() => {
                    if (!link) void open(password);
                    else setEntered(true);
                  }}
                >
                  Open the course
                </Button>
              </div>
            )}

            {link && (
              <p style={{ ...monoPlain, marginTop: 18 }}>
                Shared by {link.sharedBy} · Mauvely
              </p>
            )}
          </div>
        </Centered>
      </Page>
    );
  }

  // A protected link's password is asked for again when the reviewer
  // identifies; the gate's answer is held here, in memory only, for that.
  return (
    <ReviewerCourse link={link} token={token} password={password || undefined} slug={slug} />
  );
}

// ── the course, with its rail ────────────────────────────────────────────────

function ReviewerCourse({
  link,
  token,
  password
}: {
  link: LinkInfo;
  token: string;
  password?: string;
  slug: string;
}) {
  const { Icon, Button } = ds();
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [origin, setOrigin] = useState<string | null>(null);
  const [threads, setThreads] = useState<ThreadsWire | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [identifyFor, setIdentifyFor] = useState<IdentifyTarget | null>(null);
  const [composeFor, setComposeFor] = useState<ComposeTarget | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [identified, setIdentified] = useState(false);
  const [filter, setFilter] = useState<'open' | 'all'>('all');
  const [approved, setApproved] = useState(!!link.approvedRound);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await api.threads.list(link.projectId);
      setThreads(res);
      setLoadFailed(false);
      // Reading never needs a name; writing does. `identified` tracks which of
      // those this reader has done, so the dialog appears on their first
      // attempt to say something rather than on the way in.
      setIdentified(res.me.identified);
    } catch {
      // A failed refresh keeps the rail as it was rather than emptying it —
      // "couldn't load" and "no comments" must never look the same.
      setLoadFailed(true);
    }
  }, [link.projectId]);

  useEffect(() => {
    fetch('/api/review/config')
      .then((r) => r.json() as Promise<{ previewOrigin?: string }>)
      .then((c) => setOrigin(c.previewOrigin ?? null))
      .catch(() => setOrigin(null));
    void reload();
  }, [reload]);

  // The heartbeat behind the author's "reviewers in" pill. Identified
  // reviewers only — the server ignores anyone else — and only while the tab
  // is actually being looked at.
  useEffect(() => {
    if (!identified) return;
    const beat = () => {
      if (document.visibilityState === 'visible') {
        void api.presence(link.projectId).catch(() => undefined);
      }
    };
    beat();
    const timer = setInterval(beat, 60_000);
    return () => clearInterval(timer);
  }, [identified, link.projectId]);

  const onComment = useCallback(
    (target: ComposeTarget) => {
      setSelected(target.blockId);
      // The identify step is asked once, at the moment it is actually needed —
      // the first time they try to say something, not on the way in. Nobody
      // should have to hand over a name to read.
      if (identified) setComposeFor(target);
      else setIdentifyFor(target);
    },
    [identified]
  );

  const { course, sendSelect } = useCourseBus({
    frame,
    threads: threads?.threads ?? null,
    onComment,
    onSelect: setSelected
  });

  const select = useCallback(
    (blockId: string) => {
      setSelected(blockId);
      sendSelect(blockId);
    },
    [sendSelect]
  );

  const people = useMemo<Mentionable[]>(
    () => (threads ? mentionable(threads.reviewers) : []),
    [threads]
  );

  /** Writes need a name; open the identify dialog instead when there is none. */
  const identifiedOr = useCallback(
    (fallback: IdentifyTarget) => {
      if (identified) return true;
      setIdentifyFor(fallback);
      return false;
    },
    [identified]
  );

  const me = threads?.me ?? null;
  const caps = useMemo<RailCaps>(
    () => ({
      theme: 'reviewer',
      // The tag picker is the composer's; the chip on a card stays read-only.
      tagsInComposer: true,
      // A reviewer may resolve a thread they started — withdrawing your own
      // point is yours to do. Everyone else's stays the author's call.
      canResolve: (t) => !!me?.key && t.messages[0]?.authorKey === me.key,
      resolveDisabledTitle: 'The author resolves comments',
      estimate: (t, collapsed) => (collapsed ? 58 : 92 + t.messages.length * 60),
      composerEstimate: 208
    }),
    [me?.key]
  );

  const toggleApprove = () => {
    if (!identifiedOr('plain')) return;
    api
      .approve(link.projectId)
      .then((r) => setApproved(r.approved))
      .catch(() => undefined);
  };

  const visible = (threads?.threads ?? []).filter((t) =>
    filter === 'open' ? !t.resolvedAt : true
  );

  const onLesson = (threads?.threads ?? []).filter(
    (t) => t.scope === 'block' && t.blockId && course.boxes.some((b) => b.id === t.blockId)
  ).length;

  const myColour = threads?.reviewers.find((r) => r.key === me?.key)?.colour ?? '#886193';

  const railHeader = (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '10px 12px',
          // A toolbar inside a panel — one step off it, same neutral family.
          background: 'var(--app-panel-alt)',
          flex: 'none'
        }}
      >
        <Icon name="message-square" size={14} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>
          Comments
        </span>
        <span style={{ ...monoPlain, fontSize: 9 }}>{onLesson} on this lesson</span>
        <span style={{ flex: 1 }} />
        {(['all', 'open'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            style={{
              ...mono,
              fontSize: 9.5,
              padding: '4px 8px',
              borderRadius: RADIUS.chip,
              cursor: 'pointer',
              border: `1px solid ${filter === f ? 'var(--primary)' : 'var(--border-subtle)'}`,
              background: filter === f ? 'var(--primary-soft)' : 'transparent',
              color: filter === f ? 'var(--primary-text)' : 'var(--text-muted)'
            }}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      {!identified && (
        <p style={{ ...monoPlain, lineHeight: 1.6, padding: '10px 14px 0', flex: 'none' }}>
          Hover any block in the course and use its Comment button to leave a note.
        </p>
      )}
    </>
  );

  const G = LAYOUT.gutter;

  return (
    /*
     * The reviewer's window wears the same 2026-09-09 chrome as the author's,
     * in the light theme: the violet ground, the bar on it with no divider, and
     * the course and the comment rail as panels.
     *
     * The course inside the iframe is untouched and is not ours to touch — it
     * comes from `preview.mauvely.com`, which serves Compose's vendored runtime
     * (`web/public/rt/`) through worker/preview.ts, and two other products
     * vendor the same files.
     */
    <div
      className="mv-app"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: G,
        padding: G,
        background: 'var(--app-ground)',
        overflow: 'hidden'
      }}
    >
      <header
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '4px 8px'
        }}
      >
        <Wordmark />
        <span style={{ width: 1, height: 18, background: 'var(--border-subtle)' }} />
        <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{link.title}</span>
        <span
          style={{
            ...mono,
            fontSize: 9,
            padding: '4px 9px',
            borderRadius: RADIUS.pill,
            background: 'var(--primary-soft)',
            color: 'var(--primary-text, var(--primary))'
          }}
        >
          ROUND {link.round}
          {link.dueAt ? ` · DUE ${shortDate(link.dueAt).toUpperCase()}` : ''}
        </span>
        <span style={{ flex: 1 }} />
        {course.lessons.length > 0 && course.lesson >= 0 && (
          <span style={mono}>
            Lesson {course.lesson + 1} of {course.lessons.length}
          </span>
        )}
        {me?.name && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Avatar name={me.name} colour={myColour} size={22} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{me.name}</span>
          </span>
        )}
        <Button variant={approved ? 'signal' : 'primary'} onClick={toggleApprove}>
          {approved ? 'Round approved' : 'Approve round'}
        </Button>
      </header>

      {!noticeDismissed && (
        <div
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 16px',
            background: 'var(--primary-soft)',
            borderRadius: RADIUS.appPanel
          }}
        >
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            {link.dueAt ? (
              <>
                Your comments are due <strong>{shortDate(link.dueAt)}</strong>.{' '}
              </>
            ) : null}
            {link.sharedBy} sees them the moment you post — there&rsquo;s nothing to submit.
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setNoticeDismissed(true)}
            style={{
              background: 'none',
              border: 0,
              cursor: 'pointer',
              color: 'var(--text-muted)',
              font: 'inherit'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: G }}>
        <main
          style={{
            flex: 1,
            minWidth: 0,
            background: 'var(--app-panel)',
            borderRadius: RADIUS.appPanel,
            overflow: 'hidden'
          }}
        >
          {origin ? (
            <iframe
              ref={frame}
              title="The course"
              src={`${origin}${link.previewPath}`}
              sandbox="allow-scripts allow-forms allow-popups"
              style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
            />
          ) : (
            <p style={{ padding: 40, ...monoPlain }}>Loading the course</p>
          )}
        </main>

        <aside
          style={{
            width: LAYOUT.rail,
            flex: 'none',
            background: 'var(--app-panel)',
            borderRadius: RADIUS.appPanel,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <CommentRail
            projectId={link.projectId}
            wire={threads}
            visible={visible}
            people={people}
            tags={threads?.tags ?? []}
            caps={caps}
            course={course}
            frameRef={frame}
            selected={selected}
            composeFor={composeFor}
            filter={filter}
            header={railHeader}
            loadFailed={loadFailed}
            onRetry={() => void reload()}
            guardWrite={() => identifiedOr('plain')}
            onCompose={setComposeFor}
            onSelect={select}
            onChanged={() => void reload()}
            composerPlaceholder="What should change here? @ to tag someone."
            composerSubmitLabel="Post comment"
            overallAddLabel="Add a comment about the whole course"
          />
        </aside>
      </div>

      {identifyFor && (
        <Identify
          token={token}
          password={password}
          sharedBy={link.sharedBy}
          presetEmail={link.inviteEmail}
          onDone={async () => {
            await reload();
            if (identifyFor !== 'plain') setComposeFor(identifyFor);
            setIdentifyFor(null);
          }}
          onCancel={() => setIdentifyFor(null)}
        />
      )}
    </div>
  );
}

/**
 * Name and address, asked once.
 *
 * The copy is the mockup's, and the second line is the part that matters: it
 * says who sees this and why it is being asked. Someone handing over an address
 * to a company they may not know deserves that sentence before the field, not
 * in a policy.
 */
function Identify({
  token,
  password,
  sharedBy,
  presetEmail,
  onDone,
  onCancel
}: {
  token: string;
  password?: string;
  sharedBy: string;
  presetEmail?: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { Button, Input } = ds();
  const [name, setName] = useState('');
  const [email, setEmail] = useState(presetEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim() || !email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.links.identify(token, name.trim(), email.trim(), password);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not go through.');
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(40,28,45,.44)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 60
      }}
    >
      <div
        className="mv-pop"
        style={{
          width: 'min(460px, 100%)',
          background: 'var(--surface-card)',
          borderRadius: RADIUS.panel,
          padding: '28px 30px 24px',
          boxShadow: '0 32px 70px rgba(40,28,45,.2)'
        }}
      >
        <p style={mono}>First comment</p>
        <h2 style={{ ...display(26), marginTop: 8 }}>Who should {sharedBy} thank?</h2>
        <p style={{ color: 'var(--text-body)', marginTop: 10, lineHeight: 1.6 }}>
          Your name goes on every comment you leave, so the course team knows who asked. No
          account and no password — just this.
        </p>
        <p style={{ ...mono, marginTop: 14 }}>
          Only the course team sees this · used for reply notices
        </p>

        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          <Input
            label="Your name"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="Email"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {name.trim() && (
          <p
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 14,
              marginBottom: 0,
              fontSize: 13,
              color: 'var(--text-body)'
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                border: '1px solid var(--primary)',
                color: 'var(--primary-text, var(--primary))',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                font: '700 10px/1 Inter, system-ui, sans-serif'
              }}
            >
              {initialsFor(name)}
            </span>
            <span>
              You&rsquo;ll appear as <strong>{name.trim()}</strong>
            </span>
          </p>
        )}

        {error && <p style={{ color: 'var(--danger-500)', marginTop: 10 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !name.trim() || !email.trim()}>
            {busy ? 'Saving…' : 'Post comment'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="mv-app" style={{ minHeight: '100vh', background: 'var(--app-ground)' }}>
      {children}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        textAlign: 'center'
      }}
    >
      {children}
    </div>
  );
}
